import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { DateTime } from 'luxon';
import { PlanIOTicket } from '../entities/PlanIOTicket.js';
import { projectConfig } from '@chancenwerk/config.js';
import { logger } from '@adornis/base/logging.js';

const PLANIO_URL = 'https://chancenwerk.planio.de/';

interface PlanIOAPITicket {
  id: number;
  subject: string;
  description: string;
  status: {
    id: number;
    name: string;
  };
  tracker: {
    id: number;
    name: string;
  };
  author: {
    id: number;
    name: string;
  };
  project: {
    id: number;
    name: string;
  };
}

const USER_STORY_TRACKER_ID = 7;
const STATUS_ERLEDIGT_ID = 8;

/**
 * Calculates the PlanIO sprint ID based on year and month
 * Reference points:
 * - Sprint 2024_12 = ID 50
 * - Sprint 2025_01 = ID 51
 */
export const calculateSprintId = (year: number, month: number): number => {
  // Validate input
  if (month < 1 || month > 12) {
    throw new Error('Month must be between 1 and 12');
  }

  // Calculate months since reference point (2024_12)
  const referenceYear = 2024;
  const referenceMonth = 12;
  const referenceId = 50;

  const monthsDiff = (year - referenceYear) * 12 + (month - referenceMonth);

  return referenceId + monthsDiff;
};

/**
 * Fetches tickets for a specific sprint
 * @param params
 * @returns
 */
export const getTicketsForSprint = async (sprintId: number): Promise<PlanIOAPITicket[]> => {
  const queryParams = new URLSearchParams({
    key: projectConfig.get('PLANIO_API_KEY'),
    limit: '500',
    status_id: STATUS_ERLEDIGT_ID.toString(),
    tracker_id: USER_STORY_TRACKER_ID.toString(),
    fixed_version_id: sprintId.toString(),
  });

  const response = await fetch(`${PLANIO_URL}issues.json?${queryParams.toString()}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch tickets: ${response.statusText}`);
  }

  const data = await response.json();
  return data.issues;
};

export namespace ReleaseNotesAPI {
  export const syncTickets = registerMutation({
    type: () => Boolean,
    operationName: 'ReleaseNotesAPI_syncTickets',
    resolve: () => {
      return async () => {
        const col = await getRawCollection<PlanIOTicket>(PlanIOTicket._collectionName);

        for (let i = 0; i < 12; i++) {
          const date = DateTime.now().minus({ months: i });
          const sprintId = calculateSprintId(date.year, date.month);

          logger.info({ sprintId }, 'Syncing tickets for sprint');

          const tickets = await getTicketsForSprint(sprintId);
          const insert = tickets.map(t => ({
            _class: PlanIOTicket._class,
            _id: t.id.toString(),
            ticketID: t.id,
            subject: t.subject,
            description: t.description,
            project: t.project.name,
            author: t.author.name,
            sprint: date.startOf('month').toJSDate(),
          }));

          logger.info({ insert: insert.length }, 'Inserting tickets');

          for (const i of insert) {
            await col.updateOne(
              {
                _id: i._id as any,
              },
              { $set: i as any as PlanIOTicket },
              { upsert: true },
            );
          }
        }
      };
    },
    options: {},
    params: [],
  });

  export const ticketsEmpty = registerQuery({
    type: () => Boolean,
    operationName: 'ReleaseNotesAPI_ticketsEmpty',
    resolve: () => {
      return async () => {
        const col = await getRawCollection<PlanIOTicket>(PlanIOTicket._collectionName);
        return (await col.find({}, { projection: { _id: 1 } }).toArray()).length === 0;
      };
    },
    options: {},
    params: [],
  });
}
